/**
 * Global axios setup for MedVault. Attaches the stored Bearer token to every
 * request, rotates JWT from X-New-Token response headers, and redirects to
 * /login on 401 (expired token).
 */
import axios from "axios";

const TOKEN_KEY = "medvault_access_token";
const REFRESH_KEY = "medvault_refresh_token";
const USER_KEY = "medvault_user";
const NEW_TOKEN_HEADER = "x-new-token";

let interceptorsInstalled = false;
let onUnauthenticatedCallback = null;
let isUploadActiveCallback = null;

/** Register a function that returns true when an upload is in progress. */
export function registerUploadActiveCheck(fn) {
  isUploadActiveCallback = fn;
}

function getResponseHeader(response, name) {
  const headers = response?.headers;
  if (!headers) return null;

  if (typeof headers.get === "function") {
    return headers.get(name) ?? headers.get(name.toLowerCase()) ?? null;
  }

  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : null;
}

function applyNewTokenFromResponse(response) {
  const newToken = getResponseHeader(response, NEW_TOKEN_HEADER);
  if (!newToken || typeof newToken !== "string") return;

  const trimmed = newToken.trim();
  if (!trimmed) return;

  setAuthSession({ access_token: trimmed });
}

function shouldSkipAuthFailureHandling(url = "") {
  return (
    url.endsWith("/api/auth/login") ||
    url.endsWith("/api/auth/forgot-password") ||
    url.endsWith("/api/auth/reset-password")
  );
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  window.location.replace("/login");
}

function handleUnauthorized(url) {
  clearAuthSession();
  onUnauthenticatedCallback?.();
  if (!shouldSkipAuthFailureHandling(url)) {
    redirectToLogin();
  }
}

let refreshPromise = null; // deduplicate concurrent refresh calls

async function tryRefreshToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) return false;
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
        { refresh_token: refreshToken },
        { _skipAuthRetry: true }, // prevent interceptor loop
      );
      const { access_token, refresh_token } = res.data;
      setAuthSession({ access_token, refresh_token });
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}
export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setAuthSession({ access_token, refresh_token, user }) {
  if (access_token) {
    localStorage.setItem(TOKEN_KEY, access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
  }
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuthSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  delete axios.defaults.headers.common["Authorization"];
}

/**
 * Install global request/response interceptors. Call once at app mount.
 */
export function installAxiosAuth(onUnauthenticated) {
  onUnauthenticatedCallback = onUnauthenticated;

  const existing = getStoredToken();
  if (existing) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${existing}`;
  }

  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Request: always use the latest localStorage token (covers cross-tab login)
  axios.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response: rotate JWT from header; on 401 clear session and redirect to login
  axios.interceptors.response.use(
    (response) => {
      applyNewTokenFromResponse(response);
      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const config = error?.config;

      if (status === 401 && !config?._skipAuthRetry) {
        // If an upload is in progress, silently try to refresh before logging out
        if (isUploadActiveCallback?.()) {
          const refreshed = await tryRefreshToken();
          if (refreshed) {
            // Retry the original request with the new token
            config._skipAuthRetry = true;
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${getStoredToken()}`;
            return axios(config);
          }
        }
        handleUnauthorized(url);
      }

      return Promise.reject(error);
    }
  );
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Parse FastAPI error bodies when axios used responseType: "blob". */
export async function parseAxiosErrorDetail(error) {
  const data = error?.response?.data;
  if (!data) return null;

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) return null;
      try {
        const parsed = JSON.parse(text);
        return parsed?.detail ?? parsed?.message ?? null;
      } catch {
        return text;
      }
    } catch {
      return null;
    }
  }

  return data?.detail ?? data?.message ?? null;
}
